import {
  BankOutlined,
  CoffeeOutlined,
  ExperimentOutlined,
  GlobalOutlined,
  ShopOutlined,
  CarOutlined,
} from "@ant-design/icons";

export const LANDING_LINKS = Object.freeze({
  signup: "/signup",
  login: "/login",
  demo: "https://wa.me/message/HKIGADS5JNCAF1",
  tutorials: "https://www.youtube.com/@elsatechnologies7922",
});

const assetRoot = `${process.env.PUBLIC_URL || ""}/marketing-assets/landing`;
export const LANDING_PREVIEWS = Object.freeze({
  dashboard: {
    src: `${assetRoot}/dashboard-preview-v2.webp`,
    width: 1586,
    height: 992,
  },
  operations: {
    src: `${assetRoot}/operations-preview-v1.webp`,
    width: 1690,
    height: 931,
  },
  profit: {
    src: `${assetRoot}/profit-preview-v1.webp`,
    width: 1691,
    height: 930,
  },
  recap: {
    src: `${assetRoot}/daily-recap-preview-v1.webp`,
    width: 1730,
    height: 909,
  },
  ecommerce: {
    src: `${assetRoot}/ecommerce-preview-v1.webp`,
    width: 1710,
    height: 919,
  },
  team: {
    src: `${assetRoot}/team-access-preview-v1.webp`,
    width: 1710,
    height: 920,
  },
});

export const LANDING_SECTORS = [
  { id: "restaurant", icon: CoffeeOutlined },
  { id: "commerce", icon: ShopOutlined },
  { id: "production", icon: ExperimentOutlined },
  { id: "ecommerce", icon: GlobalOutlined },
  { id: "hotel", icon: BankOutlined },
  { id: "logistics", icon: CarOutlined },
];

export const LANDING_COPY = {
  fr: {
    metaTitle: "ELSA GESTION — Toute votre activité. Un seul logiciel.",
    metaDescription:
      "Pilotez votre activité, préparez vos opérations et comprenez vos résultats. Ventes, stock, production, e-commerce, équipes, hôtellerie et logistique dans ELSA GESTION.",
    skip: "Aller au contenu",
    solutions: "Solutions",
    pricing: "Tarifs",
    why: "Pourquoi ELSA",
    resources: "Ressources",
    tutorials: "Tutoriels",
    contact: "Contacter ELSA",
    login: "Se connecter",
    trial: "Essai gratuit",
    start: "Démarrer l’essai gratuit",
    demo: "Demander une démo",
    menu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    lightTheme: "Passer au mode clair",
    darkTheme: "Passer au mode sombre",
    heroEyebrow: "COMPRENDRE. ORGANISER. DÉCIDER.",
    heroTitle: ["Toute votre activité.", "Un seul logiciel."],
    heroBody:
      "Comprenez vos chiffres, préparez vos opérations et repérez les points à vérifier : dans un même espace de gestion.",
    heroSupport:
      "Pensé pour vos réalités métier, avec un accompagnement local.",
    computerMobile: "Sur ordinateur et mobile",
    localSupport: "Support local",
    heroPreviewTitle: "Voyez l’essentiel. Décidez de la suite.",
    previewTabs: {
      dashboard: "Piloter",
      operations: "Opérer",
      profit: "Analyser",
      ecommerce: "Vendre en ligne",
    },
    previewDescriptions: {
      dashboard:
        "Activité, trésorerie et stock : une vue d’ensemble pour orienter vos décisions.",
      operations:
        "Besoins, stock disponible et coûts prévus : vérifiez avant de préparer.",
      profit:
        "Produits, marge et charges : distinguez vos ventes de votre résultat.",
      ecommerce:
        "Du catalogue public à la commande : suivez ce qui reste à préparer.",
      recap:
        "Un bilan daté pour relire l’activité, les encaissements et les points à suivre.",
      team: "Des rôles et des établissements autorisés pour organiser le travail de l’équipe.",
    },
    previewTitles: {
      dashboard: "Tableau de bord",
      operations: "Préparation et stock",
      profit: "Compte de résultat",
      recap: "Bilan quotidien",
      ecommerce: "Boutique et commandes en ligne",
      team: "Équipe et accès",
    },
    previewAlts: {
      dashboard:
        "Aperçu illustratif du tableau de bord : ventes, achats, dépenses, trésorerie, créances et alertes de stock.",
      operations:
        "Aperçu illustratif d’une préparation de 20 portions : ingrédients nécessaires, stock manquant et coûts prévus.",
      profit:
        "Aperçu illustratif du compte de résultat : produits, coût des revenus, marge brute et charges d’exploitation.",
      recap:
        "Aperçu illustratif du bilan du 3 octobre 2026 : ventes, encaissements reçus et ruptures de stock constatées dans ce bilan.",
      ecommerce:
        "Aperçu illustratif d’un catalogue public et d’une commande de trois articles en préparation.",
      team: "Aperçu illustratif des collaborateurs et des accès de caisse limités à un établissement autorisé.",
    },
    illustration: "Aperçu illustratif · Données de démonstration",
    enlarge: "Agrandir l’aperçu",
    closePreview: "Fermer l’aperçu",
    sectorsTitle: "Votre métier, votre façon de travailler.",
    sectorsHint: "Explorez les usages qui vous concernent.",
    sectorDetail: "ELSA pour votre métier",
    closeSector: "Fermer le détail métier",
    sectorCta: "Parlons de votre activité",
    sectors: {
      restaurant: {
        name: "Restaurant",
        short: "Salle, caisse et cuisine",
        title: "De la commande à la préparation, gardez le fil.",
        body: "Prenez les commandes, encaissez au POS et préparez vos recettes en tenant compte des ingrédients disponibles.",
        bullets: [
          "Commandes en salle, au comptoir ou à emporter",
          "Recettes, quantités à préparer et besoins en stock",
          "Bilan de l’activité et des encaissements",
        ],
      },
      commerce: {
        name: "Commerce",
        short: "Ventes, achats et stock",
        title: "Vendez sans perdre de vue ce qu’il reste.",
        body: "Rassemblez les ventes, achats, retours et paiements pour suivre votre activité commerciale et les mouvements de stock.",
        bullets: [
          "Ventes et encaissements au comptoir",
          "Achats, retours et disponibilités des produits",
          "Créances clients et montants restant à payer",
        ],
      },
      production: {
        name: "Production",
        short: "Fabrication et coûts",
        title: "Vérifiez vos besoins avant de lancer.",
        body: "Préparez vos opérations à partir des recettes et composants, avec les quantités nécessaires et les coûts prévus.",
        bullets: [
          "Recettes et composants de production",
          "Besoins calculés selon la quantité demandée",
          "Suivi des préparations et des coûts",
        ],
      },
      ecommerce: {
        name: "E-commerce",
        short: "Boutique et commandes",
        title: "Une boutique en ligne reliée à votre gestion.",
        body: "Présentez votre catalogue dans la boutique publique et retrouvez les commandes dans votre espace de gestion.",
        bullets: [
          "Catalogue et présentation des produits",
          "Commandes en ligne et statuts de préparation",
          "Informations de livraison et suivi des commandes",
        ],
      },
      hotel: {
        name: "Hôtellerie",
        short: "Réservations et séjours",
        title: "Retrouvez les informations utiles à chaque séjour.",
        body: "Organisez les réservations, les chambres et les opérations liées aux clients dans le module hôtellerie.",
        bullets: [
          "Disponibilités des chambres et réservations",
          "Clients, tarifs et services du séjour",
          "Folios, paiements et dépenses de l’activité",
        ],
      },
      logistics: {
        name: "Logistique",
        short: "Dossiers et opérations",
        title: "Suivez vos dossiers au-delà d’une simple liste.",
        body: "Regroupez vos dossiers et opérations logistiques avec leurs informations de suivi et leurs éléments financiers.",
        bullets: [
          "Dossiers et opérations de l’activité logistique",
          "Informations et pièces associées aux dossiers",
          "Suivi des dépenses et des paiements",
        ],
      },
    },
    benefitsTitle: "Moins de zones d’ombre. Plus de maîtrise.",
    benefitsBody:
      "Des réponses concrètes aux questions qui rythment votre activité.",
    chapters: [
      {
        id: "operations",
        preview: "operations",
        eyebrow: "01 · OPÉRATIONS & STOCK",
        title: "Le stock suffit-il avant de lancer ?",
        body: "Avant une préparation ou une fabrication, confrontez la quantité demandée aux ingrédients disponibles et aux coûts prévus.",
        bullets: [
          "Identifiez les composants manquants avant de lancer.",
          "Ajustez la quantité ou complétez votre stock.",
        ],
        note: "Et pour le commerce : ventes, achats et mouvements de stock restent dans le même espace.",
        action: "Découvrir les opérations",
        sector: "production",
      },
      {
        id: "rentabilite",
        preview: "profit",
        eyebrow: "02 · RENTABILITÉ",
        title: "Vous vendez. Mais qu’est-ce qu’il vous reste ?",
        body: "Le chiffre d’affaires ne dit pas tout. Distinguez vos produits, vos coûts, votre marge et vos charges dans les rapports de gestion.",
        bullets: [
          "Consultez vos résultats par période et par établissement.",
          "Retrouvez le détail derrière les montants.",
        ],
        note: "Des indicateurs pour comprendre, pas seulement des chiffres à additionner.",
        action: "Explorer les rapports",
      },
      {
        id: "bilan",
        preview: "recap",
        eyebrow: "03 · BILAN QUOTIDIEN",
        title: "En fin de journée, ne restez pas dans le flou.",
        body: "Retrouvez un récapitulatif daté de votre activité, de votre trésorerie et des points à surveiller, adapté à votre profil métier.",
        bullets: [
          "Distinguez les ventes des encaissements reçus.",
          "Revenez sur un bilan et ses points d’attention.",
        ],
        note: "Les alertes d’un bilan décrivent la situation à sa date, pas le stock en temps réel.",
        action: "Voir un exemple de bilan",
      },
      {
        id: "boutique",
        preview: "ecommerce",
        eyebrow: "04 · E-COMMERCE",
        title: "Vos commandes en ligne ne doivent pas rester à part.",
        body: "Présentez vos produits dans votre boutique publique et suivez les commandes dans votre espace de gestion, jusqu’à leur préparation et leur livraison.",
        bullets: [
          "Un catalogue pour rendre votre offre accessible.",
          "Des commandes à suivre, sans perdre leur contexte.",
        ],
        note: "La boutique et la gestion se complètent : chacune garde son rôle.",
        action: "Découvrir la vente en ligne",
        sector: "ecommerce",
      },
      {
        id: "equipes",
        preview: "team",
        eyebrow: "05 · ÉQUIPES & MULTISITE",
        title: "Votre activité ne doit pas reposer sur vous seul.",
        body: "Organisez le travail de votre équipe avec des rôles et des accès par entreprise et établissement. Chacun retrouve son contexte de travail.",
        bullets: [
          "Attribuez les rôles et les établissements autorisés.",
          "Gardez un contexte clair pour chaque opération.",
        ],
        note: "Les ressources humaines complètent la gestion de vos collaborateurs.",
        action: "Découvrir l’organisation en équipe",
      },
    ],
    onboardingTitle: "Un démarrage accompagné, à votre rythme.",
    onboardingBody:
      "Partons de votre façon de travailler pour préparer un espace qui vous ressemble.",
    onboardingSteps: [
      {
        title: "Présentez votre activité",
        body: "Échangeons sur vos besoins, votre organisation et les situations que vous voulez mieux maîtriser.",
      },
      {
        title: "Configurez votre espace",
        body: "Préparez vos produits, vos établissements et les accès de votre équipe.",
      },
      {
        title: "Prenez vos repères",
        body: "Appuyez-vous sur une démonstration, les tutoriels et l’accompagnement local.",
      },
    ],
    roleAccess: "Accès selon les rôles",
    pricingTitle: "Une formule adaptée à votre organisation.",
    pricingBody:
      "Consultez les offres disponibles et choisissez votre rythme de facturation. Les montants sont issus de la configuration de souscription ELSA.",
    pricingButton: "Consulter les tarifs",
    pricingClose: "Masquer les tarifs",
    pricingLabel: "Période de facturation",
    monthly: "Mensuel",
    annual: "Annuel",
    priceUnitMonth: "/ établissement / mois",
    priceUnitYear: "/ établissement / an",
    pricingScope:
      "Prix pour un établissement. Le total de votre souscription dépend de vos établissements actifs et de l’offre choisie.",
    priceLoading: "Chargement des offres…",
    priceUnavailable:
      "Les offres en ligne sont temporairement indisponibles. Votre essai reste accessible ; vous pouvez aussi contacter un conseiller.",
    retry: "Réessayer",
    choosePlan: "Choisir",
    usersIncluded: "utilisateurs inclus",
    companiesIncluded: "entreprise(s) incluse(s)",
    saving: "Économie",
    instead: "Au lieu de",
    quoteUnavailable: "Tarif indisponible pour cette période",
    noPlanDescription: "Une offre pour organiser et piloter votre activité.",
    paymentOnTitle: "Souscription en ligne disponible",
    paymentOnBody:
      "Après la création du compte, le parcours de paiement YengaPay existant prend le relais. La souscription est confirmée après validation du paiement.",
    paymentOffTitle: "Votre essai reste accessible",
    paymentOffBody:
      "Le paiement en ligne n’est pas activé actuellement. Vous pouvez créer votre compte d’essai ou échanger avec ELSA.",
    faqTitle: "Quelques réponses avant de commencer.",
    faqs: [
      {
        question: "ELSA convient-il à mon activité ?",
        answer:
          "ELSA réunit plusieurs modules : commerce, restauration et production, e-commerce, hôtellerie, logistique et ressources humaines. Une démonstration permet de vérifier les usages qui correspondent à votre organisation.",
      },
      {
        question: "Puis-je gérer plusieurs établissements ?",
        answer:
          "Oui. La gestion distingue les entreprises et les établissements, avec des accès attribués aux utilisateurs. Les conditions de votre offre sont précisées dans les tarifs disponibles.",
      },
      {
        question: "Comment démarrer avec mon équipe ?",
        answer:
          "Créez votre compte, préparez les informations de votre activité puis attribuez les rôles et les établissements accessibles aux collaborateurs. Vous pouvez demander une démonstration et consulter les tutoriels.",
      },
      {
        question: "Comment fonctionne la boutique en ligne ?",
        answer:
          "Le module e-commerce permet de présenter un catalogue public et de retrouver les commandes dans l’espace de gestion. La configuration de la boutique, des livraisons et des paiements se prépare selon vos besoins.",
      },
    ],
    closingTitle: "Moins d’incertitude. Plus de maîtrise.",
    closingBody: "Voyons comment ELSA peut s’intégrer à votre activité.",
    footerBody:
      "Comprendre votre activité. Organiser vos opérations. Décider de la suite.",
    legal: "Mentions légales",
    privacy: "Confidentialité",
    rights: "Tous droits réservés.",
  },
  en: {
    metaTitle: "ELSA GESTION — Your whole business. One workspace.",
    metaDescription:
      "Understand your business, plan operations and review results. Sales, stock, production, e-commerce, teams, hospitality and logistics with ELSA GESTION.",
    skip: "Skip to content",
    solutions: "Solutions",
    pricing: "Pricing",
    why: "Why ELSA",
    resources: "Resources",
    tutorials: "Tutorials",
    contact: "Contact ELSA",
    login: "Sign in",
    trial: "Free trial",
    start: "Start your free trial",
    demo: "Request a demo",
    menu: "Open menu",
    closeMenu: "Close menu",
    lightTheme: "Switch to light mode",
    darkTheme: "Switch to dark mode",
    heroEyebrow: "UNDERSTAND. ORGANISE. DECIDE.",
    heroTitle: ["Your whole business.", "One workspace."],
    heroBody:
      "Understand your figures, plan your operations and spot what needs attention — in one management workspace.",
    heroSupport: "Built around the way you work, with local support.",
    computerMobile: "On desktop and mobile",
    localSupport: "Local support",
    heroPreviewTitle: "See what matters. Decide what’s next.",
    previewTabs: {
      dashboard: "Manage",
      operations: "Operate",
      profit: "Analyse",
      ecommerce: "Sell online",
    },
    previewDescriptions: {
      dashboard:
        "Activity, cash and stock: an overview to guide your decisions.",
      operations:
        "Requirements, available stock and expected costs: check before you prepare.",
      profit:
        "Revenue, margin and expenses: distinguish sales from your result.",
      ecommerce:
        "From your public catalogue to the order: follow what still needs preparing.",
      recap: "A dated recap of activity, receipts and points to follow up.",
      team: "Roles and authorised locations to organise your team’s work.",
    },
    previewTitles: {
      dashboard: "Dashboard",
      operations: "Preparation and stock",
      profit: "Income statement",
      recap: "Daily recap",
      ecommerce: "Online store and orders",
      team: "Team and access",
    },
    previewAlts: {
      dashboard:
        "Illustrative dashboard showing sales, purchases, expenses, cash balances, receivables and stock alerts. Demo screen in French.",
      operations:
        "Illustrative preparation of 20 portions showing ingredient needs, missing stock and expected costs. Demo screen in French.",
      profit:
        "Illustrative income statement showing revenue, cost of revenue, gross margin and operating expenses. Demo screen in French.",
      recap:
        "Illustrative recap for 3 October 2026 showing sales, receipts and stock outages recorded in that recap. Demo screen in French.",
      ecommerce:
        "Illustrative public catalogue and a three-item order being prepared. Demo screen in French.",
      team: "Illustrative team roles and cashier access limited to an authorised location. Demo screen in French.",
    },
    illustration: "Illustrative preview · Demo data · Screens in French",
    enlarge: "Enlarge preview",
    closePreview: "Close preview",
    sectorsTitle: "Your business, your way of working.",
    sectorsHint: "Explore the uses that matter to you.",
    sectorDetail: "ELSA for your business",
    closeSector: "Close business details",
    sectorCta: "Let’s talk about your business",
    sectors: {
      restaurant: {
        name: "Restaurant",
        short: "Dining, checkout and kitchen",
        title: "Keep track from order to preparation.",
        body: "Take orders, receive payments at the POS and prepare recipes with available ingredients in mind.",
        bullets: [
          "Dine-in, counter and takeaway orders",
          "Recipes, preparation quantities and stock requirements",
          "Activity and receipts recap",
        ],
      },
      commerce: {
        name: "Retail",
        short: "Sales, purchases and stock",
        title: "Sell while keeping available stock in sight.",
        body: "Bring sales, purchases, returns and payments together to follow commercial activity and stock movements.",
        bullets: [
          "Counter sales and receipts",
          "Purchases, returns and product availability",
          "Customer receivables and outstanding balances",
        ],
      },
      production: {
        name: "Production",
        short: "Manufacturing and costs",
        title: "Check your requirements before starting.",
        body: "Plan operations from recipes and components, with required quantities and expected costs.",
        bullets: [
          "Recipes and production components",
          "Requirements based on the requested quantity",
          "Preparation and cost tracking",
        ],
      },
      ecommerce: {
        name: "E-commerce",
        short: "Store and orders",
        title: "An online store connected to management.",
        body: "Present your public catalogue and find incoming orders in your management workspace.",
        bullets: [
          "Catalogue and product presentation",
          "Online orders and preparation status",
          "Delivery information and order tracking",
        ],
      },
      hotel: {
        name: "Hospitality",
        short: "Reservations and stays",
        title: "Find useful information for every stay.",
        body: "Organise reservations, rooms and guest-related operations in the hospitality module.",
        bullets: [
          "Room availability and reservations",
          "Guests, rates and stay services",
          "Folios, payments and business expenses",
        ],
      },
      logistics: {
        name: "Logistics",
        short: "Files and operations",
        title: "Follow your files beyond a simple list.",
        body: "Group logistics files and operations with their tracking information and financial details.",
        bullets: [
          "Logistics files and operations",
          "Information and documents attached to files",
          "Expense and payment tracking",
        ],
      },
    },
    benefitsTitle: "Fewer blind spots. More control.",
    benefitsBody: "Practical answers to the questions that shape your day.",
    chapters: [
      {
        id: "operations",
        preview: "operations",
        eyebrow: "01 · OPERATIONS & STOCK",
        title: "Is there enough stock before you start?",
        body: "Before preparation or manufacturing, compare the requested quantity with available ingredients and expected costs.",
        bullets: [
          "Identify missing components before starting.",
          "Adjust the quantity or replenish stock.",
        ],
        note: "For retail too: sales, purchases and stock movements stay in the same workspace.",
        action: "Explore operations",
        sector: "production",
      },
      {
        id: "rentabilite",
        preview: "profit",
        eyebrow: "02 · PROFITABILITY",
        title: "You’re selling. But what’s left?",
        body: "Revenue does not tell the whole story. Distinguish revenue, costs, margin and expenses in management reports.",
        bullets: [
          "Review results by period and location.",
          "Find the detail behind the amounts.",
        ],
        note: "Indicators to understand, not just figures to add up.",
        action: "Explore reports",
      },
      {
        id: "bilan",
        preview: "recap",
        eyebrow: "03 · DAILY RECAP",
        title: "Don’t end the day in the dark.",
        body: "Find a dated recap of activity, cash flows and points to watch, adapted to your business profile.",
        bullets: [
          "Distinguish sales from payments received.",
          "Revisit a recap and its points of attention.",
        ],
        note: "Recap alerts describe the situation on that date, not live stock.",
        action: "See a sample recap",
      },
      {
        id: "boutique",
        preview: "ecommerce",
        eyebrow: "04 · E-COMMERCE",
        title: "Your online orders shouldn’t stand apart.",
        body: "Present products in your public store and follow orders in the management workspace, through preparation and delivery.",
        bullets: [
          "A catalogue to make your offer accessible.",
          "Orders to follow without losing their context.",
        ],
        note: "The store and management complement each other: each keeps its role.",
        action: "Explore online selling",
        sector: "ecommerce",
      },
      {
        id: "equipes",
        preview: "team",
        eyebrow: "05 · TEAMS & MULTI-SITE",
        title: "Your business shouldn’t depend on you alone.",
        body: "Organise teamwork with roles and access by company and location. Everyone finds their working context.",
        bullets: [
          "Assign roles and authorised locations.",
          "Keep a clear context for every operation.",
        ],
        note: "Human resources complements the management of your staff.",
        action: "Explore teamwork",
      },
    ],
    onboardingTitle: "A supported start, at your own pace.",
    onboardingBody:
      "Let’s start with the way you work to prepare a workspace that fits.",
    onboardingSteps: [
      {
        title: "Tell us about your business",
        body: "Discuss your needs, your organisation and the situations you want to manage better.",
      },
      {
        title: "Configure your workspace",
        body: "Prepare products, locations and your team’s access.",
      },
      {
        title: "Find your bearings",
        body: "Use a demonstration, tutorials and local support.",
      },
    ],
    roleAccess: "Role-based access",
    pricingTitle: "A plan that fits your organisation.",
    pricingBody:
      "Review available plans and choose your billing period. Amounts come from ELSA’s subscription configuration.",
    pricingButton: "View pricing",
    pricingClose: "Hide pricing",
    pricingLabel: "Billing period",
    monthly: "Monthly",
    annual: "Annual",
    priceUnitMonth: "/ location / month",
    priceUnitYear: "/ location / year",
    pricingScope:
      "Price for one location. Your subscription total depends on your active locations and selected plan.",
    priceLoading: "Loading plans…",
    priceUnavailable:
      "Online plans are temporarily unavailable. You can still start your trial or contact an adviser.",
    retry: "Try again",
    choosePlan: "Choose",
    usersIncluded: "users included",
    companiesIncluded: "company/companies included",
    saving: "Saving",
    instead: "Instead of",
    quoteUnavailable: "Price unavailable for this period",
    noPlanDescription: "A plan to organise and manage your business.",
    paymentOnTitle: "Online subscription available",
    paymentOnBody:
      "After account creation, the existing YengaPay payment flow takes over. Subscription is confirmed after payment validation.",
    paymentOffTitle: "Your trial remains available",
    paymentOffBody:
      "Online payment is not currently enabled. You can create a trial account or contact ELSA.",
    faqTitle: "A few answers before you begin.",
    faqs: [
      {
        question: "Does ELSA fit my business?",
        answer:
          "ELSA brings together retail, restaurant and production, e-commerce, hospitality, logistics and human resources modules. A demo helps check which uses fit your organisation.",
      },
      {
        question: "Can I manage multiple locations?",
        answer:
          "Yes. Management distinguishes companies and locations, with access assigned to users. Your plan’s conditions are shown in available pricing.",
      },
      {
        question: "How do I start with my team?",
        answer:
          "Create your account, prepare your business information and assign roles and accessible locations to staff. You can request a demonstration and consult tutorials.",
      },
      {
        question: "How does the online store work?",
        answer:
          "The e-commerce module lets you present a public catalogue and find orders in your management workspace. Store, delivery and payment configuration is prepared around your needs.",
      },
    ],
    closingTitle: "Less uncertainty. More control.",
    closingBody: "Let’s see how ELSA can fit into your business.",
    footerBody:
      "Understand your activity. Organise operations. Decide what’s next.",
    legal: "Legal notice",
    privacy: "Privacy",
    rights: "All rights reserved.",
  },
};
